/**
 * /disk-image-inspector orchestrator.
 *
 * Format detector → conditional render of MBR/GPT/header/ISO/UDF panels +
 * hex viewer + history + hash button + JSON/TXT/PDF report export.
 */
(function (w) {
    'use strict';
    var DI = w.__DiskImage;
    var I18N = w.DiskImageInspectorI18n || {};
    var ae = function (n, p) { try { if (typeof w.__ae === 'function') w.__ae(n, p || {}); } catch (_) {} };
    var toast = w.__toast || function (m) { console.log(m); };
    var handleErr = w.__handleError || function (e) { console.error(e); };
    var t = function (k, fb) { return I18N[k] || fb || k; };

    function _human(n) {
        if (n < 1024) return n + ' B';
        if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
        if (n < 1024 * 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + ' MB';
        return (n / 1024 / 1024 / 1024).toFixed(2) + ' GB';
    }

    function App(root) {
        this.root = root;
        this.file = null;
        this.detection = null;
        this.summary = null;
        this.dom = {};
    }

    App.prototype.boot = function () {
        this._cacheDom();
        this._wire();
        this._loadHistory();
    };

    App.prototype._cacheDom = function () {
        var r = this.root;
        this.dom.empty = r.querySelector('.dii-empty');
        this.dom.workspace = r.querySelector('.dii-workspace');
        this.dom.summary = r.querySelector('.dii-summary');
        this.dom.tree = r.querySelector('.dii-tree');
        this.dom.detail = r.querySelector('.dii-detail');
        this.dom.btnHash = r.querySelector('.dii-btn-hash');
        this.dom.btnCancelHash = r.querySelector('.dii-btn-hash-cancel');
        this.dom.btnExportJson = r.querySelector('.dii-btn-json');
        this.dom.btnExportTxt = r.querySelector('.dii-btn-txt');
        this.dom.btnReset = r.querySelector('.dii-btn-reset');
        this.dom.history = r.querySelector('.dii-history');
        this.dom.progress = r.querySelector('.dii-progress');
        this.dom.progressText = r.querySelector('.dii-progress-text');
        this.dom.btnHex = r.querySelector('.dii-btn-hex');
        this.dom.btnExtract = r.querySelector('.dii-btn-extract');
        this.dom.hexBox = r.querySelector('.dii-hex');
    };

    App.prototype._wire = function () {
        var self = this;
        var dropEl = document.getElementById('diiDrop') || this.root.querySelector('[data-uploader-id]');
        if (dropEl) {
            // Shared-uploader contract event. This tool shipped listening to
            // `uploader:filesready` (dispatched by nothing) + a raw input
            // `change` fallback that reads an already-cleared FileList — both
            // dead, so no file could enter the tool. See bin-cue-to-iso.js
            // (bug 2026-07-07) for the full account. Never wire the input.
            dropEl.addEventListener('tool-uploader:file', function (e) {
                var d = e.detail || {};
                var f = d.file || (d.files && d.files[0]);
                if (f) self.inspect(f);
            });
        }
        if (this.dom.btnHash) this.dom.btnHash.addEventListener('click', function () { self.hashAll(); });
        if (this.dom.btnCancelHash) this.dom.btnCancelHash.addEventListener('click', function () { self.cancelHash(); });
        if (this.dom.btnExportJson) this.dom.btnExportJson.addEventListener('click', function () { self.exportReport('json'); });
        if (this.dom.btnExportTxt) this.dom.btnExportTxt.addEventListener('click', function () { self.exportReport('txt'); });
        if (this.dom.btnReset) this.dom.btnReset.addEventListener('click', function () { self.reset(); });
        if (this.dom.btnHex) this.dom.btnHex.addEventListener('click', function () { self.toggleHex(); });
        if (this.dom.btnExtract) this.dom.btnExtract.addEventListener('click', function () { self.openInExtractor(); });
    };

    App.prototype.reset = function () {
        this.file = null;
        this.detection = null;
        this.summary = null;
        if (this.dom.empty) this.dom.empty.style.display = '';
        if (this.dom.workspace) { this.dom.workspace.style.display = 'none'; this.dom.workspace.classList.remove('active'); }
    };

    App.prototype.inspect = function (file) {
        var self = this;
        self.file = file;
        self._showProgress(t('inspecting', 'Inspecting…'));
        var t0 = Date.now();
        ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_start', file_size_mb: Math.round(file.size / 1024 / 1024), file_ext: (file.name.split('.').pop() || '').toLowerCase() });
        var reader = new DI.BinaryReader(file);
        return DI.detect(file).then(function (det) {
            self.detection = det;
            return self._buildSummary(reader, det);
        }).then(function () {
            self._hideProgress();
            self._renderMounted();
            self._appendHistory();
            ae('feature_use', {
                slug: 'disk-image-inspector',
                action: 'inspect_done',
                format: self.detection.format,
                ms: Date.now() - t0,
                has_mbr: !!(self.summary.mbr && self.summary.mbr.signatureOk),
                has_gpt: !!self.summary.gpt,
                has_iso: !!self.summary.iso,
                has_udf: !!self.summary.udf,
                partitions_count: self.summary.partitions.length,
                bootable: !!(self.summary.boot && self.summary.boot.bootable)
            });
        }).catch(function (err) {
            self._hideProgress();
            if (w.ToolErrors && w.ToolErrors.isFileLostError && w.ToolErrors.isFileLostError(err)) {
                toast(t('file_lost_at_select', 'Could not read the file. Please select it again.'), 'error');
                if (self.dom.empty) self.dom.empty.style.display = '';
                ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_file_lost', where: 'inspect' });
                return;
            }
            handleErr(err, 'inspect');
            ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_format_unknown', file_ext: (file.name.split('.').pop() || '').toLowerCase() });
        });
    };

    App.prototype._buildSummary = function (reader, det) {
        var self = this;
        var summary = {
            file: self.file,
            format: det.format,
            detection: det,
            mbr: null, gpt: null, iso: null, udf: null, boot: null,
            partitions: [],
            header: null,
            hashes: null
        };
        var promises = [];
        // Always probe MBR + GPT — they can coexist with ISO etc
        promises.push(DI.parseMbr(reader).then(function (m) { summary.mbr = m; }).catch(function () {}));
        promises.push(DI.parseGpt(reader).then(function (g) { summary.gpt = g; }).catch(function () {}));
        // Format-specific header parsers
        if (det.format === 'vhd' && DI.headers && DI.headers.vhd) {
            promises.push(DI.headers.vhd(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }
        if (det.format === 'vhdx' && DI.headers && DI.headers.vhdx) {
            promises.push(DI.headers.vhdx(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }
        if (det.format === 'vmdk' && DI.headers && DI.headers.vmdk) {
            promises.push(DI.headers.vmdk(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }
        if (det.format === 'qcow2' && DI.headers && DI.headers.qcow2) {
            promises.push(DI.headers.qcow2(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }
        if (det.format === 'dmg' && DI.headers && DI.headers.dmg) {
            promises.push(DI.headers.dmg(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }
        if (det.format === 'e01' && DI.headers && DI.headers.e01) {
            promises.push(DI.headers.e01(reader).then(function (h) { summary.header = h; }).catch(function () {}));
        }

        // ISO9660 PVD if applicable
        if (det.format === 'iso9660' || det.format === 'iso9660+udf') {
            promises.push((function () {
                var iso = new DI.Iso9660(reader);
                return iso.mount().then(function () {
                    summary.iso = { pvd: iso.pvd, jolietApplied: !!iso.svdLba, brLba: iso.brLba };
                    if (DI.parseBoot) return DI.parseBoot(reader, iso.brLba).then(function (b) { summary.boot = b; });
                }).catch(function () {});
            })());
        }
        // UDF anchor probe
        if (det.format === 'udf' || det.format === 'iso9660+udf') {
            promises.push((function () {
                if (!DI.Udf) return;
                var udf = new DI.Udf(reader);
                return udf._findAvdp().then(function (avdp) {
                    summary.udf = { avdp: avdp, version: udf.version };
                }).catch(function () {});
            })());
        }

        return Promise.all(promises).then(function () {
            // Resolve filesystem fingerprint per partition
            var parts = [];
            if (summary.gpt && summary.gpt.partitions.length) {
                parts = summary.gpt.partitions.map(function (p) { return { source: 'gpt', startLba: p.startLba, sizeBytes: p.sizeBytes, name: p.name, type: p.typeName, typeId: p.typeGuid }; });
            } else if (summary.mbr && summary.mbr.partitions.length) {
                parts = summary.mbr.partitions.map(function (p) { return { source: 'mbr', startLba: p.startLba, sizeBytes: p.sizeBytes, type: p.typeName, typeId: p.type }; });
            }
            var fsP = parts.map(function (p) {
                return DI.fingerprint(reader, p.startLba).then(function (fp) { p.fs = fp; return p; }).catch(function () { p.fs = { fs: 'unknown' }; return p; });
            });
            return Promise.all(fsP).then(function (resolved) {
                summary.partitions = resolved;
                self.summary = summary;
            });
        });
    };

    App.prototype._renderMounted = function () {
        if (this.dom.empty) this.dom.empty.style.display = 'none';
        // `.active` releases app.css's workspace-preview gate — without it the
        // shared [class$=-workspace]:not(.active) rule hides every child.
        if (this.dom.workspace) { this.dom.workspace.style.display = ''; this.dom.workspace.classList.add('active'); }
        this._renderSummary();
        this._renderTree();
    };

    App.prototype._renderSummary = function () {
        var s = this.summary;
        var d = this.dom.summary;
        if (!d || !s) return;
        var html = '<div><b>' + t('format', 'Format') + ':</b> <code>' + this._esc(s.format) + '</code> ';
        html += '<span class="dii-confidence">' + (Math.round((s.detection.confidence || 0) * 100)) + '%</span>';
        // Say when the format is a NAME guess rather than a magic-byte match —
        // an inspector that presents a guess like a reading is worse than one
        // that admits it (format-detector.js sets basis).
        if (s.detection.basis === 'extension') {
            html += ' <span class="dii-note">' + this._esc(t('format_by_extension', 'guessed from the file name — no known signature in the bytes')) + '</span>';
        }
        html += '</div>';
        html += '<div><b>' + t('size', 'Size') + ':</b> ' + _human(s.file.size) + '</div>';
        if (s.iso && s.iso.pvd) {
            html += '<div><b>' + t('iso_volume', 'ISO Volume') + ':</b> <code>' + this._esc(s.iso.pvd.volumeId) + '</code></div>';
        }
        if (s.udf) {
            html += '<div><b>UDF:</b> v' + this._esc(s.udf.version || 'detected') + '</div>';
        }
        if (s.boot && s.boot.bootable) {
            html += '<div><b>' + t('bootable', 'Bootable') + ':</b> ' + (s.boot.platforms || []).join(', ') + '</div>';
        }
        d.innerHTML = html;
    };

    App.prototype._renderTree = function () {
        var d = this.dom.tree;
        if (!d) return;
        var s = this.summary;
        var html = '';
        if (s.mbr && s.mbr.partitions.length) {
            html += '<h3>MBR</h3>';
            html += '<div><b>' + t('disk_signature', 'Disk Signature') + ':</b> 0x' + ((s.mbr.diskSignature || 0) >>> 0).toString(16) + '</div>';
            if (s.mbr.isProtective) html += '<div class="dii-tag">' + t('protective_mbr', 'Protective MBR') + '</div>';
            if (s.mbr.isHybrid) html += '<div class="dii-tag">' + t('hybrid_mbr', 'Hybrid MBR') + '</div>';
            html += '<table class="dii-parts"><tr><th>#</th><th>Type</th><th>Start LBA</th><th>Size</th><th>Boot</th></tr>';
            s.mbr.partitions.forEach(function (p) {
                html += '<tr><td>' + p.index + '</td><td>' + p.typeName + '</td><td>' + p.startLba + '</td><td>' + _human(p.sizeBytes) + '</td><td>' + (p.bootable ? '✓' : '') + '</td></tr>';
            });
            html += '</table>';
        }
        if (s.gpt) {
            html += '<h3>GPT</h3>';
            html += '<div><b>' + t('disk_guid', 'Disk GUID') + ':</b> <code>' + this._esc(s.gpt.header.diskGuid) + '</code></div>';
            html += '<div><b>CRC:</b> ' + (s.gpt.header.crcOk === true ? '<span class="dii-ok">✓</span>' : (s.gpt.header.crcOk === false ? '<span class="dii-bad">✗</span>' : '?')) + '</div>';
            html += '<table class="dii-parts"><tr><th>#</th><th>Type</th><th>Name</th><th>Start LBA</th><th>Size</th></tr>';
            s.gpt.partitions.forEach(function (p) {
                html += '<tr><td>' + p.index + '</td><td>' + p.typeName + '</td><td>' + this._esc(p.name) + '</td><td>' + p.startLba + '</td><td>' + _human(p.sizeBytes) + '</td></tr>';
            }, this);
            html += '</table>';
        }
        if (s.partitions.length) {
            html += '<h3>' + t('filesystems', 'Filesystems') + '</h3>';
            html += '<ul class="dii-fs-list">';
            s.partitions.forEach(function (p) {
                html += '<li>' + this._esc(p.name || p.type) + ' → <b>' + (p.fs.fs || 'unknown') + '</b>';
                if (p.fs.label) html += ' (' + this._esc(p.fs.label) + ')';
                if (p.fs.uuid) html += ' [<code>' + this._esc(p.fs.uuid) + '</code>]';
                html += '</li>';
            }, this);
            html += '</ul>';
        }
        if (s.header) {
            html += '<h3>' + t('container_header', 'Container header') + '</h3>';
            html += '<pre class="dii-json">' + this._esc(JSON.stringify(s.header, _bytesReplacer, 2)) + '</pre>';
        }
        if (s.hashes) {
            html += '<h3>' + t('hashes', 'Hashes') + '</h3>';
            html += '<table class="dii-hashes">';
            Object.keys(s.hashes).forEach(function (algo) {
                html += '<tr><td>' + algo.toUpperCase() + '</td><td><code class="dii-hash">' + s.hashes[algo] + '</code></td></tr>';
            });
            html += '</table>';
        }
        d.innerHTML = html;
    };

    function _bytesReplacer(key, val) {
        if (val instanceof Uint8Array) return '<' + val.byteLength + ' bytes>';
        return val;
    }

    App.prototype.toggleHex = function () {
        var self = this;
        if (!self.dom.hexBox || !self.summary) return;
        if (self._hex) {
            self._hex.dispose();
            self._hex = null;
            self.dom.hexBox.style.display = 'none';
            return;
        }
        if (!w.__DiskImage.HexViewer) { toast('Hex viewer not loaded', 'error'); return; }
        self.dom.hexBox.style.display = '';
        var reader = new w.__DiskImage.BinaryReader(self.summary.file);
        self._hex = new w.__DiskImage.HexViewer(reader, self.dom.hexBox, { height: 380 });
        // Apply highlights based on what was detected
        var highlights = [];
        var H = w.__DiskImage.HexHighlights;
        if (self.summary.mbr && self.summary.mbr.signatureOk && H && H.mbr) {
            highlights = highlights.concat(H.mbr(self.summary.mbr));
        }
        if (self.summary.gpt && H && H.gpt) {
            highlights = highlights.concat(H.gpt());
        }
        if (self.summary.iso && H && H.pvd) {
            highlights = highlights.concat(H.pvd());
        }
        // Add per-partition highlights for first 4KB of each partition
        if (self.summary.partitions) {
            self.summary.partitions.forEach(function (p, i) {
                var off = (p.startLba || 0) * 512;
                if (off > 0) {
                    highlights.push({
                        name: (p.name || p.type || 'partition') + ' boot sector (' + (p.fs && p.fs.fs || '?') + ')',
                        start: off,
                        end: off + 512
                    });
                }
            });
        }
        self._hex.highlight(highlights);
        ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_hex_opened', highlights: highlights.length });
    };

    App.prototype.openInExtractor = function () {
        var self = this;
        if (!self.file || !w.__DiskImage.Handoff) return;
        var fmt = self.detection && self.detection.format;
        if (fmt !== 'iso9660' && fmt !== 'iso9660+udf' && fmt !== 'udf') {
            toast(t('not_iso_for_extractor', 'Open-in-extractor only available for ISO9660 / UDF images'), 'warning');
            return;
        }
        ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_open_in_sibling', target: 'iso-extractor' });
        w.__DiskImage.Handoff.send('iso-extractor', { file: self.file, sourceSlug: 'disk-image-inspector' });
    };

    /**
     * Hash the whole image. This tool advertises 16 GB+ images, and four digests
     * over 16 GB is minutes of work — so it must be interruptible. The streamer
     * has always accepted an AbortSignal; the tool simply never passed one.
     */
    App.prototype.hashAll = function () {
        var self = this;
        if (!self.file || !DI.Hash) return;
        if (self._hashAbort) return; // already running
        self._hashAbort = (typeof AbortController === 'function') ? new AbortController() : null;
        self._showProgress(t('hashing', 'Hashing…'));
        if (self.dom.btnCancelHash) self.dom.btnCancelHash.style.display = self._hashAbort ? '' : 'none';
        if (self.dom.btnHash) self.dom.btnHash.disabled = true;
        var t0 = Date.now();
        var done = function () {
            self._hashAbort = null;
            self._hideProgress();
            if (self.dom.btnCancelHash) self.dom.btnCancelHash.style.display = 'none';
            if (self.dom.btnHash) self.dom.btnHash.disabled = false;
        };
        ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_hash_start', size_mb: Math.round(self.file.size / 1024 / 1024) });
        return DI.Hash.multiHash(self.file, ['sha-256', 'sha-1', 'md5', 'crc32'], function (p) {
            self._showProgress(t('hashing', 'Hashing…') + ' ' + ((p.processed && p.total) ? Math.round(100 * p.processed / p.total) + '%' : '—'));
        }, self._hashAbort ? self._hashAbort.signal : null).then(function (out) {
            done();
            self.summary.hashes = out;
            self._renderTree();
            ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_hash_done', algos: Object.keys(out), ms: Date.now() - t0 });
        }).catch(function (err) {
            done();
            if (err && err.name === 'AbortError') {
                ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_hash_cancelled', ms: Date.now() - t0 });
                return;
            }
            if (w.ToolErrors && w.ToolErrors.isFileLostError && w.ToolErrors.isFileLostError(err)) {
                toast(t('file_lost_at_select', 'Could not read the file. Please select it again.'), 'error');
                ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_file_lost', where: 'hash' });
                return;
            }
            handleErr(err, 'hash');
        });
    };

    App.prototype.cancelHash = function () {
        if (this._hashAbort) this._hashAbort.abort();
    };

    App.prototype.exportReport = function (kind) {
        if (!this.summary) return;
        var s = this.summary;
        var name = (s.file.name || 'image') + '.report.' + kind;
        var content;
        if (kind === 'json') {
            var clone = JSON.parse(JSON.stringify({
                format: s.format,
                size: s.file.size,
                mbr: s.mbr ? { signatureOk: s.mbr.signatureOk, isProtective: s.mbr.isProtective, isHybrid: s.mbr.isHybrid, diskSignature: s.mbr.diskSignature, partitions: s.mbr.partitions } : null,
                gpt: s.gpt ? { header: s.gpt.header, partitions: s.gpt.partitions } : null,
                iso: s.iso ? { pvd: { volumeId: s.iso.pvd.volumeId, systemId: s.iso.pvd.systemId, volumeSpaceSize: s.iso.pvd.volumeSpaceSize } } : null,
                udf: s.udf,
                boot: s.boot,
                partitions: s.partitions,
                header: s.header,
                hashes: s.hashes
            }, _bytesReplacer));
            content = new Blob([JSON.stringify(clone, null, 2)], { type: 'application/json' });
        } else {
            var lines = [];
            lines.push('Disk Image Inspector report');
            lines.push('File: ' + s.file.name + ' (' + _human(s.file.size) + ')');
            lines.push('Format: ' + s.format);
            if (s.mbr && s.mbr.signatureOk) lines.push('MBR: ' + s.mbr.partitions.length + ' partitions, hybrid=' + s.mbr.isHybrid);
            if (s.gpt) lines.push('GPT: ' + s.gpt.partitions.length + ' partitions, CRC=' + s.gpt.header.crcOk);
            if (s.iso) lines.push('ISO9660 volume: ' + s.iso.pvd.volumeId);
            if (s.udf) lines.push('UDF: v' + (s.udf.version || 'detected'));
            if (s.boot && s.boot.bootable) lines.push('Bootable: ' + (s.boot.platforms || []).join(', '));
            if (s.hashes) {
                Object.keys(s.hashes).forEach(function (a) { lines.push(a.toUpperCase() + ': ' + s.hashes[a]); });
            }
            s.partitions.forEach(function (p) {
                lines.push('  ' + (p.name || p.type) + ' → ' + p.fs.fs + ' [' + (p.fs.label || '') + ']');
            });
            content = new Blob([lines.join('\n')], { type: 'text/plain' });
        }
        // The shared primitive — the only save channel that reports real delivery
        // and the only one that is not a silent no-op in an Android WebView.
        if (typeof w.__downloadFile === 'function') {
            w.__downloadFile(content, name);
        } else {
            var url = URL.createObjectURL(content);
            var a = document.createElement('a');
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click();
            setTimeout(function () { try { document.body.removeChild(a); } catch (_) {} URL.revokeObjectURL(url); }, 4000);
        }
        ae('feature_use', { slug: 'disk-image-inspector', action: 'inspect_report_export', kind: kind });
    };

    App.prototype._appendHistory = function () {
        if (!this.summary || !DI.Persistence) return;
        DI.Persistence.append('inspector_history', {
            fileName: this.summary.file.name,
            fileSize: this.summary.file.size,
            format: this.summary.format,
            partitions: (this.summary.partitions || []).length,
            bootable: !!(this.summary.boot && this.summary.boot.bootable)
        }).catch(function () {});
    };
    App.prototype._loadHistory = function () {
        var self = this;
        if (!DI.Persistence || !this.dom.history) return;
        DI.Persistence.list('inspector_history', 10).then(function (items) {
            if (!items || !items.length) { self.dom.history.innerHTML = ''; return; }
            var html = '<h3>' + t('history_title', 'Recent inspections') + '</h3><ul class="dii-history-list">';
            items.forEach(function (it) {
                html += '<li><b>' + self._esc(it.fileName) + '</b> — ' + self._esc(it.format) + ' · ' + _human(it.fileSize) + '</li>';
            });
            html += '</ul>';
            self.dom.history.innerHTML = html;
        }).catch(function () {});
    };

    App.prototype._showProgress = function (txt) {
        if (this.dom.progress) this.dom.progress.style.display = '';
        if (this.dom.progressText) this.dom.progressText.textContent = txt || '';
    };
    App.prototype._hideProgress = function () { if (this.dom.progress) this.dom.progress.style.display = 'none'; };

    App.prototype._esc = function (s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    };

    w.__DiskImageInspector = { App: App };

    document.addEventListener('DOMContentLoaded', function () {
        var root = document.querySelector('.dii-root');
        if (!root) return;
        var app = new App(root);
        app.boot();
        w.__DiskImageInspectorApp = app;
        // (No Timbrica3D.init: that is the 3D-printing core and a no-op here —
        // telemetry comes from telemetry-tier0.js plus the __ae calls above.)
    });
})(window);
